/**
 * Confirm-plan — the pure answer to "which legs of this order can be confirmed
 * right now, and why not for the rest?"
 *
 * Under the hood every order is one or more legs. A one-leg order is simply the
 * n = 1 case. This module takes the *stored* data (an `orders` row plus its
 * `legs` rows) — never form state — and produces a per-leg verdict:
 *
 *   - `already`  — that leg is past `booked`; nothing to do.
 *   - `blocked`  — it is missing something, listed in `reasons`.
 *   - `eligible` — it can be confirmed now.
 *
 * It is deliberately free of React, Supabase and async work: the caller resolves
 * trailer types, eligibility rows, OOS sets and the documentation gate up front
 * and passes them in. That keeps the whole decision unit-testable and means the
 * button, the modal and the write path can only ever agree.
 */
import { missingConfirmRequirements } from "@/lib/confirm-readiness";
import {
  blockReasonForTripType,
  formatEligibleModes,
  ELIGIBILITY_MISSING_LABEL,
  type EligibilityGateState,
  type TruckEligibilityRow,
} from "@/lib/truck-eligibility";

/** Statuses a leg can hold. Mirrors `public.order_status`. */
export type LegStatus =
  | "booked"
  | "confirmed"
  | "tender_received"
  | "in_transit"
  | "delivered"
  | "canceled";

/** The subset of a `public.legs` row this decision needs. */
export interface ConfirmPlanLeg {
  id: string;
  /** 1-based position. A single-leg order passes 1. */
  legSequence: number;
  status: LegStatus | string | null;
  assignedTractorId?: string | null;
  tractorLabel?: string | null;
  /** `legs.assigned_operator_id` — required when the leg's trip type is Manual. */
  assignedOperatorId?: string | null;

  trailerId?: string | null;
  trailerTypeId?: string | null;
  tripType?: string | null;
  /** Set when this leg already has an active external trip. */
  eapiTripId?: string | null;
}

export type LegConfirmState = "eligible" | "blocked" | "already" | "canceled";

export interface LegConfirmVerdict {
  legId: string;
  legSequence: number;
  state: LegConfirmState;
  /** Human-readable blockers. Empty unless `state === "blocked"`. */
  reasons: string[];
}

export interface ConfirmPlanInput {
  /** `orders.status`. Used only for reporting, never as a per-leg answer. */
  orderStatus?: LegStatus | string | null;
  legs: readonly ConfirmPlanLeg[];
  /** True for trailer types whose `kind` is `bobtail` (trailer not required). */
  isBobtailTrailerType?: (trailerTypeId: string | null | undefined) => boolean;
  /** AAM truck-eligibility rows, keyed by tractor id. */
  eligibilityByTractor?: Map<string, TruckEligibilityRow>;
  eligibilityGateState?: EligibilityGateState;
  /** Out-of-service tractor ids, keyed by leg id. */
  oosBlockedByLegId?: ReadonlyMap<string, ReadonlySet<string>>;
  /**
   * Order-level documentation blockers (BOL / tender / load-status mismatch).
   * Applied to every leg, because documents are order-scoped and the gate must
   * run on each confirmation — see the confirm-doc-gating module.
   */
  documentBlockers?: readonly string[];
  /** Admin acknowledged the documentation warning for this action. */
  documentOverride?: boolean;
  /**
   * Admin acknowledged an override of the policy gates for this action: truck
   * eligibility, out-of-service and documentation. Missing equipment is never
   * overridable — the dispatch system cannot create a trip without it. Admins
   * who need to skip dispatch entirely use the Mark In Transit override.
   */
  adminOverride?: boolean;
  /**
   * State of the caller's leg query. An empty leg list means "no legs" only
   * when the load actually finished — otherwise the tooltip claimed a real
   * multi-leg order had no legs while its stops were still loading.
   */
  legsLoadState?: "ready" | "loading" | "error";
}

export interface ConfirmPlan {
  legs: LegConfirmVerdict[];
  /** Leg ids that can be confirmed right now. */
  eligibleLegIds: string[];
  /** True when at least one leg can be confirmed. */
  canConfirmAny: boolean;
  /** True when every non-cancelled leg is already past `booked`. */
  allConfirmed: boolean;
  /** More than one non-cancelled leg ⇒ the picker modal is worth showing. */
  isMultiLeg: boolean;
  /**
   * Why the Confirm action as a whole is unavailable, or null when it is.
   * Reads the same as the per-leg reasons, so tooltip and action agree.
   */
  blockedReason: string | null;
}

const CONFIRMABLE_FROM: ReadonlySet<string> = new Set(["booked"]);

function legLabel(leg: ConfirmPlanLeg, multi: boolean): string {
  return multi ? `Leg ${leg.legSequence}` : "This order";
}

/**
 * Evaluates every leg. Fails closed: an order with no legs, or a leg whose
 * supporting data is unknown, reports as blocked rather than eligible.
 */
export function buildConfirmPlan(input: ConfirmPlanInput): ConfirmPlan {
  const isBobtail = input.isBobtailTrailerType ?? (() => false);
  const gateState: EligibilityGateState = input.eligibilityGateState ?? "ready";
  const eligibility = input.eligibilityByTractor ?? new Map<string, TruckEligibilityRow>();
  const docBlockers = input.documentOverride || input.adminOverride ? [] : input.documentBlockers ?? [];

  const active = input.legs.filter((l) => l.status !== "canceled");
  const multi = active.length > 1;

  if (input.legs.length === 0) {
    const loadState = input.legsLoadState ?? "ready";
    return {
      legs: [],
      eligibleLegIds: [],
      canConfirmAny: false,
      allConfirmed: false,
      isMultiLeg: false,
      blockedReason:
        loadState === "loading"
          ? "Still loading this order's stops. Try again in a moment."
          : loadState === "error"
            ? "This order's stops couldn't be loaded. Refresh and try again."
            : "This order has no legs to confirm.",
    };
  }

  const verdicts: LegConfirmVerdict[] = input.legs.map((leg) => {
    if (leg.status === "canceled") {
      return { legId: leg.id, legSequence: leg.legSequence, state: "canceled", reasons: [] };
    }
    if (!CONFIRMABLE_FROM.has(String(leg.status ?? ""))) {
      return { legId: leg.id, legSequence: leg.legSequence, state: "already", reasons: [] };
    }

    const reasons: string[] = [];
    const where = legLabel(leg, multi);

    // 1. Asset presence — shared with the button's readiness rule.
    const missing = missingConfirmRequirements({
      legs: [
        {
          tractorId: leg.assignedTractorId,
          trailerId: leg.trailerId,
          trailerTypeId: leg.trailerTypeId,
          tripType: leg.tripType,
          operatorId: leg.assignedOperatorId,
        },
      ],
      isBobtailTrailerType: isBobtail,
    });
    for (const m of missing) {
      const article = m === "operator" ? "an" : "a";
      reasons.push(`${where} is missing ${article} ${m}.`);
    }


    // 2. Truck eligibility (AAM). Fails closed when data is missing/unknown.
    if (leg.assignedTractorId && gateState !== "off" && !input.adminOverride) {
      if (gateState !== "ready") {
        reasons.push(
          gateState === "loading"
            ? "Truck eligibility is still loading. Try again in a moment."
            : "Truck eligibility data is unavailable right now.",
        );
      } else {
        const row = eligibility.get(leg.assignedTractorId);
        const reason = blockReasonForTripType(row, leg.tripType ?? null);
        if (reason === "missing" || reason === "unknown") {
          reasons.push(`${leg.tractorLabel || "The assigned truck"} (${where}): ${ELIGIBILITY_MISSING_LABEL}.`);
        } else if (reason === "mode") {
          reasons.push(
            `${leg.tractorLabel || "The assigned truck"} (${where}) is not eligible for ${leg.tripType} trips. Eligible modes: ${formatEligibleModes(row)}.`,
          );
        }
      }
    }

    // 3. Out of service for this leg's own window.
    const oos = input.oosBlockedByLegId?.get(leg.id);
    if (!input.adminOverride && leg.assignedTractorId && oos?.has(leg.assignedTractorId)) {
      reasons.push(
        `${leg.tractorLabel || "The assigned truck"} is out of service for ${where.toLowerCase()}'s scheduled window.`,
      );
    }

    // 4. Order-level documentation gate, enforced on every leg confirmation.
    for (const blocker of docBlockers) reasons.push(blocker);

    return {
      legId: leg.id,
      legSequence: leg.legSequence,
      state: reasons.length > 0 ? "blocked" : "eligible",
      reasons,
    };
  });

  const eligibleLegIds = verdicts.filter((v) => v.state === "eligible").map((v) => v.legId);
  const nonCanceled = verdicts.filter((v) => v.state !== "canceled");
  const allConfirmed = nonCanceled.length > 0 && nonCanceled.every((v) => v.state === "already");

  let blockedReason: string | null = null;
  if (eligibleLegIds.length === 0) {
    if (allConfirmed) blockedReason = "Every leg of this order is already confirmed.";
    else {
      const firstBlocked = verdicts.find((v) => v.state === "blocked");
      blockedReason = firstBlocked?.reasons[0] ?? "This order can't be confirmed right now.";
    }
  }

  return {
    legs: verdicts,
    eligibleLegIds,
    canConfirmAny: eligibleLegIds.length > 0,
    allConfirmed,
    isMultiLeg: multi,
    blockedReason,
  };
}

/**
 * Should the parent order be promoted to `confirmed`?
 *
 * Only once every non-cancelled leg is past `booked`. Confirming a subset of a
 * multi-leg order deliberately leaves the parent at `booked`.
 *
 * Fails closed on an unknown/legacy leg status (null or empty): a stop written
 * before per-leg status existed must never read as "already confirmed", or
 * confirming one stop would silently promote the whole order.
 */
export function shouldPromoteParent(
  legStatusesAfterWrite: readonly (LegStatus | string | null)[],
): boolean {
  const active = legStatusesAfterWrite.filter((s) => s !== "canceled");
  if (active.length === 0) return false;
  return active.every((s) => {
    const v = String(s ?? "").trim();
    if (!v) return false;
    return !CONFIRMABLE_FROM.has(v);
  });
}

/**
 * The blockers an admin override would waive: every reason on a leg that is
 * blocked in `plan` but eligible in `overridePlan`. Used for the
 * acknowledgement dialog and the audit trail, so both list the same thing.
 */
export function overriddenReasons(plan: ConfirmPlan, overridePlan: ConfirmPlan): string[] {
  const unlocked = new Set(
    overridePlan.eligibleLegIds.filter((id) => !plan.eligibleLegIds.includes(id)),
  );
  const out: string[] = [];
  for (const v of plan.legs) {
    if (!unlocked.has(v.legId)) continue;
    for (const r of v.reasons) if (!out.includes(r)) out.push(r);
  }
  return out;
}
